import { create } from "~/graphql-client";
import { CartItem, CartItemDetailed, Product, ProductCategory } from "~/types";
import { getCartItemTotal, mapCartDetails } from "./cart";

type TransactionFetcher = ReturnType<typeof create>["getTransaction"];
type MenuFetcher = ReturnType<typeof create>["getMenus"];

// Analytics is secondary to product performance
function quietFbq(
  eventType: string,
  eventName: string,
  parameters:
    | facebook.Pixel.AddToCartParameters
    | facebook.Pixel.InitiateCheckoutParameters
    | facebook.Pixel.CustomParameters,
  eventOptions?: facebook.Pixel.EventIDOptions,
) {
  if (!window.fbq) {
    return;
  }
  try {
    fbq(eventType, eventName, parameters, eventOptions);
  } catch (err) {
    console.error(`skipped fbq tracking event: ${err}`);
  }
}
function quietGtag(...params: Parameters<Gtag.Gtag>) {
  if (!window.gtag) {
    return;
  }
  try {
    gtag(...params);
  } catch (err) {
    console.error(`skipped gtag tracking event: ${err}`);
  }
}

function detailedCartItemsToFacebookContents(cartDetails: CartItemDetailed[]) {
  return cartDetails.map((item) => {
    const { amount } = getCartItemTotal(item, item.product);
    return {
      id: item.productId,
      quantity: item.quantity,
      name: item.product?.title ?? "Unknown Item",
      price: amount / 100,
    };
  });
}
function detailedCartItemsToGoogleEcommerce(
  cartDetails: CartItemDetailed[],
  locationRef: string,
) {
  return cartDetails.map((item) => {
    const { amount, taxIncluded } = getCartItemTotal(item);
    return {
      item_id: item.productId,
      item_name: item.product?.title ?? "Unknown Item",
      location_id: locationRef,
      price: (amount - (taxIncluded || 0)) / 100,
      quantity: item.quantity,
    };
  });
}

function productsToGoogleEcommerce(
  cartDetails: Product[],
  locationRef: string,
) {
  return cartDetails.map((product) => {
    return {
      item_id: product.id,
      item_name: product.title,
      location_id: locationRef,
      price: (product.price.amount - (product.price.taxIncluded || 0)) / 100,
    };
  });
}

export async function addCartItemAnalyticsEvent(
  currency: string,
  locationRef: string,
  cartItems: CartItem[],
  getMenus: MenuFetcher,
) {
  cartEvent(
    "AddToCart",
    "add_to_cart",
    currency,
    locationRef,
    cartItems,
    getMenus,
  );
}

export async function initiateCheckoutAnalyticsEvent(
  currency: string,
  locationRef: string,
  cartItems: CartItem[],
  getMenus: MenuFetcher,
) {
  cartEvent(
    "InitiateCheckout",
    "begin_checkout",
    currency,
    locationRef,
    cartItems,
    getMenus,
  );
}

export async function viewItemAnalyticsEvent(
  currency: string,
  locationRef: string,
  cartItems: CartItem[],
  getMenus: MenuFetcher,
) {
  cartEvent(
    "ViewContent",
    "view_item",
    currency,
    locationRef,
    cartItems,
    getMenus,
  );
}

export async function viewItemListAnalyticsEvent(
  listName: string,
  currency: string,
  locationRef: string,
  products: Product[],
) {
  quietGtag("event", "view_item_list", {
    currency: currency,
    items: productsToGoogleEcommerce(products, locationRef),
    item_list_name: listName,
  });

  // No equivalent for Facebook
}

export async function selectItemAnalyticsEvent(
  listName: string,
  locationRef: string,
  products: Product[],
) {
  quietGtag("event", "select_item", {
    items: productsToGoogleEcommerce(products, locationRef),
    item_list_name: listName,
  });

  // No equivalent for Facebook
}

export async function cartEvent(
  eventNameFB: string,
  eventNameGtag: string,
  currency: string,
  locationRef: string,
  cartItems: CartItem[],
  getMenus: MenuFetcher,
) {
  if (!window.gtag && !window.fbq) return;
  const menuResult = await getMenus(
    locationRef,
    null,
    cartItems.map((cartItem) => cartItem.productId),
  );
  const flatMenu = menuResult.ordering?.menus?.flatMap(
    (menu): ProductCategory[] => menu.categories || [],
  );

  if (!flatMenu) return;

  const { cartTotalPrice, cartDetails } = mapCartDetails(cartItems, flatMenu);

  quietFbq("track", eventNameFB, {
    currency: currency,
    value: cartTotalPrice.amount / 100, // Meta doesn't have a field for tax, so we'll send tax inclusive
    contents: detailedCartItemsToFacebookContents(cartDetails),
  });

  quietGtag("event", eventNameGtag, {
    currency: currency,
    value: (cartTotalPrice.amount - (cartTotalPrice.taxIncluded || 0)) / 100,
    tax: (cartTotalPrice.taxIncluded || 0) / 100,
    items: detailedCartItemsToGoogleEcommerce(cartDetails, locationRef),
  });
}

export async function purchaseAnalyticsEvent(
  transactionId: string,
  transactionFetcher: TransactionFetcher,
) {
  if (!window.gtag && !window.fbq) return;

  const transactionResult = await transactionFetcher(transactionId);
  const transaction = transactionResult.order;
  if (!transaction) return;

  const flattenedItems = transaction.items
    .map((item) => {
      if (!item) return;
      let totalAmount = item.amount;
      let totalTax = item.tax || 0;
      item.options?.forEach((option) => {
        option?.choices.forEach((choice) => {
          totalAmount += choice?.amount || 0;
          totalTax += choice?.tax || 0;
        });
      });

      return {
        name: item.name,
        quantity: item.quantity,
        amount: totalAmount,
        tax: totalTax,
      };
    })
    .filter(<T>(item: T | undefined): item is T => item !== undefined);

  quietFbq(
    "track",
    "Purchase",
    {
      currency: transaction.currency,
      value: transaction.itemTotal / 100,
      contents: flattenedItems.map((item) => ({
        quantity: item.quantity,
        name: item.name,
        price: item.amount / 100,
      })),
    },
    { eventID: transactionId },
  );

  quietGtag("event", "purchase", {
    transaction_id: transactionId,
    value: transaction.itemTotal / 100,
    tax: (transaction.tax || 0) / 100,
    currency: transaction.currency,
    coupon: transaction.discounts?.map((discount) => discount?.name).join(" ,"),
    orderTime: new Date(),
    orderType: transaction.billingType,
    orderScheduled: transaction.fulfillAt,
    discount:
      (transaction.discounts?.reduce(
        (sum, discount) => sum + (discount?.amount || 0),
        0,
      ) || 0) / 100,
    items: flattenedItems.map((item) => ({
      item_name: item?.name,
      price: item?.amount / 100,
      quantity: item?.quantity,
    })),
  });
}
